import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { MapPin, TrendingUp } from "lucide-react";
import { CardStatusTag, CardSaveButton } from "./CardHeaderActions";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatPriceExact, getPrimaryImg } from "@/utils";
import { Transfer } from "@/types";
import { useAuthStore } from "@/store/authStore";
import { PERMISSIONS } from "@/routes/guards";
import { useToggleSaveListing } from "@/hooks/useSavedListings";

type Props = {
  item: Transfer;
};

export default function TransferCard({ item }: Props) {
  const { user } = useAuthStore();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const saved = isSaved(item.id);
  const img = getPrimaryImg(item.images);

  // Chỉ hiển thị tag trạng thái cho Seller/Admin. Buyer và Guest không cần thấy.
  const showStatusTag = !!user && PERMISSIONS.canCreateTransfer(user.role);

  return (
    <Link
      to={`/sang-nhuong/${item.id}`}
      className="fnb-card block animate-fade-up h-full flex flex-col relative"
    >
      {/* Thumbnail */}
      <div className="h-44 overflow-hidden rounded-t-xl relative shrink-0">
        {img ? (
          <img
            src={img}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-6xl"
            style={{ background: "var(--brand-pale)" }}
          >
            🏪
          </div>
        )}

        <CardStatusTag
          className="absolute top-2 left-2 z-10"
          show={showStatusTag}
          status={item.approvalStatus}
          label={item.approvalStatus === "approved" ? "Đã duyệt" : item.approvalStatus === "rejected" ? "Từ chối" : "Chờ duyệt"}
        />
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <h3
          className="font-semibold text-sm leading-snug line-clamp-2 mb-3 pr-8"
          style={{ color: "var(--text-1)" }}
        >
          {item.title}
        </h3>

        {/* Price */}
        <p
          className="font-display font-black text-lg mb-3"
          style={{ color: "var(--brand)" }}
        >
          {formatPriceExact(item.price)} {item.currencyUnit}
        </p>

        {/* Revenue */}
        <div className="flex gap-3 mb-3 text-xs">
          <span
            className="flex items-center gap-1"
            style={{ color: "var(--text-3)" }}
          >
            <TrendingUp size={11} />
            Doanh thu: {formatPriceExact(item.revenue)} {item.currencyUnit}/tháng
          </span>
        </div>

        {/* Type badges */}
        <div className="flex gap-1.5 mb-3 flex-wrap">
          <Badge variant="secondary" className="text-[10px]">
            {item.businessTypeName}
          </Badge>
          <Badge variant="secondary" className="text-[10px]">
            {item.businessSectorName}
          </Badge>
        </div>

        {/* Meta */}
        <div
          className="flex flex-wrap gap-1.5 pt-3 border-t mt-auto pr-10"
          style={{ borderColor: "var(--border-c)" }}
        >
          <span className="fnb-tag text-[11px]">
            <MapPin size={10} /> {item.district?.name}, {item.province?.name}
          </span>

          {item.area > 0 && (
            <span className="fnb-tag text-[11px]">
              {item.area} {item.areaUnit}
            </span>
          )}
        </div>
      </div>

      <CardSaveButton
        className="absolute bottom-4 right-4 z-10"
        saved={saved}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSave(item.id, "BusinessTransfer");
        }}
      />
    </Link>
  );
}
